// Or from '@reduxjs/toolkit/query' if not using the auto-generated hooks
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { apiConfig } from "../../apiConfig";
import { getHeaders, ApiScope } from "../silentAuth";
import { CreateSubscriptionCommand, GetSubscriptionUsageResult } from "./types";
import { ISubscription } from "@oqdo/automationapps/Memberships/Subscription";
import { ICompany } from "@oqdo/automationapps/Agents/Organizations/Company";
import { IStripeSubscription } from "@oqdo/automationapps/Memberships/StripeSubscription";
import { IPerson } from "@oqdo/automationapps/Agents/Person";
import { PagedResult } from "../types";

// Define a service using a base URL and expected endpoints
export const subscriptionApi = createApi({
  reducerPath: "subscriptionApi",
  baseQuery: fetchBaseQuery({
    baseUrl: apiConfig.subscriptionApiUrl,
    prepareHeaders: async (headers, { getState }) => {
      return await getHeaders(ApiScope, headers);
    },
  }),
  tagTypes: [
    "Subscriptions",
    "SubscriptionUsage",
    "StripeSubscriptions",
    "SubscriptionsOwner",
    "SubscriptionsAdmins",
    "SubscriptionEvents",
    "SubscriptionUsage",
  ],
  endpoints: (builder) => ({
    getSubscriptionById: builder.query<ISubscription, string>({
      query: (id) => `subscriptions/${id}`,
      providesTags: ["Subscriptions"],
    }),
    getSubscriptionOwner: builder.query<ICompany, string>({
      query: (id) => `subscriptions/${id}/owner`,
      providesTags: ["SubscriptionsOwner"],
    }),
    getStripeSubscriptionById: builder.query<IStripeSubscription | undefined, string>({
      query: (id) => `stripesubscriptions/${id}`,
      providesTags: ["StripeSubscriptions"],
    }),
    getSubscriptions: builder.query<PagedResult<ISubscription>, { page: number; pageSize: number }>({
      query: ({ page, pageSize }) => `subscriptions?page=${page}&pageSize=${pageSize}`,
      providesTags: ["Subscriptions"],
    }),
    getSubscriptionEvents: builder.query<
      PagedResult<any>,
      { id: string; start: string; end: string; page: number; pageSize: number }
    >({
      query: ({ id, start, end, page, pageSize }) =>
        `subscriptions/${id}/events?page=${page}&pageSize=${pageSize}&start=${start}&end=${end}`,
      providesTags: ["SubscriptionEvents"],
    }),
    createSubscription: builder.mutation<any, CreateSubscriptionCommand>({
      query: (obj) => ({
        url: `subscriptions/`,
        method: "post",
        body: obj,
        responseHandler: (response: { text: () => any }) => response.text(),
      }),
      invalidatesTags: ["Subscriptions"],
    }),
    setSubscriptionOwner: builder.mutation<any, { id: string; tenantId: string }>({
      query: (args) => ({
        url: `subscriptions/${args.id}/owner`,
        method: "post",
        body: args,
        responseHandler: (response: { text: () => any }) => response.text(),
      }),
      onQueryStarted: async function (props, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        setTimeout(() => {
          dispatch(subscriptionApi.util.invalidateTags(["SubscriptionsOwner"]));
        }, 5000);
      },
      invalidatesTags: ["SubscriptionsOwner"],
    }),
    updateSubscription: builder.mutation<any, { id: string; data: Partial<ISubscription> }>({
      query: (args) => ({
        url: `subscriptions/` + args.id,
        method: "patch",
        body: args.data,
      }),
      invalidatesTags: ["Subscriptions"],
    }),
    deleteSubscription: builder.mutation<any, string>({
      query: (args) => ({
        url: `subscriptions/` + args,
        method: "delete",
      }),
      onQueryStarted: async function (props, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        setTimeout(() => {
          dispatch(subscriptionApi.util.invalidateTags(["Subscriptions"]));
        }, 5000);
      },
      invalidatesTags: ["Subscriptions"],
    }),

    getSubscriptionAdmins: builder.query<IPerson[], string>({
      query: (id) => `subscriptions/${id}/admins`,
      providesTags: ["SubscriptionsAdmins"],
    }),
    addSubscriptionAdmin: builder.mutation<any, { subscriptionId: string; email: string; displayName: string }>({
      query: (obj) => ({
        url: `subscriptions/` + obj.subscriptionId + "/admins",
        method: "post",
        body: obj,
        responseHandler: (response: { text: () => any }) => response.text(),
      }),
      onQueryStarted: async function (props, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        setTimeout(() => {
          dispatch(subscriptionApi.util.invalidateTags(["SubscriptionsAdmins"]));
        }, 5000);
      },
      invalidatesTags: ["SubscriptionsAdmins"],
    }),
    deleteSubscriptionAdmin: builder.mutation<any, { subscriptionId: string; userId: string }>({
      query: (args) => ({
        url: `subscriptions/` + args.subscriptionId + "/admins/" + args.userId,
        method: "delete",
        responseHandler: (response: { text: () => any }) => response.text(),
      }),
      onQueryStarted: async function (props, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        setTimeout(() => {
          dispatch(subscriptionApi.util.invalidateTags(["SubscriptionsAdmins"]));
        }, 5000);
      },
      invalidatesTags: ["SubscriptionsAdmins"],
    }),
    getSubscriptionUsage: builder.query<GetSubscriptionUsageResult[], string>({
      query: (id) => `subscription-usage/${id}`,
      providesTags: ["SubscriptionUsage"],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetSubscriptionByIdQuery,
  useGetSubscriptionsQuery,
  useCreateSubscriptionMutation,
  useDeleteSubscriptionMutation,
  useUpdateSubscriptionMutation,
  useGetStripeSubscriptionByIdQuery,
  useGetSubscriptionOwnerQuery,
  useSetSubscriptionOwnerMutation,
  useGetSubscriptionAdminsQuery,
  useAddSubscriptionAdminMutation,
  useDeleteSubscriptionAdminMutation,
  useGetSubscriptionEventsQuery,
  useGetSubscriptionUsageQuery,
} = subscriptionApi;
